/**
 * **장비 설치 신청 — 어휘·말·거르기·넘김 한 곳**(설계안 2판 `docs/planning/subscription-billing-admin-20261021.md` §9·§10.2·§11.2).
 * 의존성 없는 순수 모듈이다(Vue·Element·`@/` 별칭 금지 — 단위 시험이 node 에서 그대로 읽는다).
 *
 * 장비 신청은 **구독과 별개 기능**이다(§9.0) — 이 화면은 구독·요금제·체험을 읽지 않는다.
 *
 * 서버와의 계약(§11.2, 관리 세션 admin·mng):
 *
 * - `GET /api/admin/equipment-requests` — 질의 `status`(한 값, 없으면 전체) · `from`·`to`(신청일 `YYYY-MM-DD`, KST, 포함) · `q`
 *   · `overdue=true` · `installedFrom`·`installedTo`(설치 완료일, KST, 포함 — 「이번 달 설치 완료」 카드) + 페이징·정렬
 *   (`utils/pagedList` 봉투, 정렬 축 `requestedDtm`). 행은 표 `tbl_equipment_request`(§9.2)의 칸을 camelCase 로 싣고
 *   `overdueHours`(계산값)를 더한다.
 * - `GET …/summary` — `{ newCount, contactedCount, scheduledCount, installedThisMonth, overdueCount }`. `installedThisMonth` 는
 *   `status=INSTALLED` + 이번 달 1일~말일(KST)의 `totalElements` 와 같다.
 * - `POST …/{id}/contacted`·`schedule`·`installed`·`cancel` — 본문 `{}`·`{installDate}`·`{}`·`{reason}`. 200 행 · 400 · 404 ·
 *   409 `STATUS_CHANGED`(그 사이 다른 사람이 바꿨다). `schedule` 은 설치일 확정(SCHEDULED)에서도 받는다 — 설치일 다시 정하기
 *   (10-21 사용자 결정 E1).
 * - **직접 추가**(§9.7, 10-23 사용자 요청) — `GET …/prefill?storeCd=` → `{ storeCd, storeNm, storeId, ceoNm, registeredTel, address,
 *   contactNeeded, open: <행>|null }`(관리자는 전체 번호를 본다 — 숫자만, 없으면 빈 글자) · `POST /api/admin/equipment-requests`
 *   `{ storeCd, idempotencyKey, contactTel?, address?, memo? }` → 201 `{ request: <행> }` · 400 `INVALID_REQUEST`(입력 오류, 없는 매장도
 *   — 서버에 매장 없음 코드가 없다) · 409 `REQUEST_OPEN`(+`request` — 그 매장의 열린 신청). 같은 매장·같은 멱등 키는 201 재생(같은 신청, 알림톡
 *   없음). 판정·저장·알림톡은 POS 신청과 같은 서버 한 곳이다 — 입력 검사([addRequestOf])는 그 규칙(`EquipmentRequestRules.submission`)의
 *   거울이고 서버가 정본이다.
 *
 * **「연락 전 넘김」 판정은 서버가 소유한다**(`overdueHours` — 기한은 서버 운영값이다, §1.4 N14). 화면은 값이 있으면 빨간 글씨로
 * 「(N시간)」을 붙일 뿐 기한을 다시 셈하지 않는다. 서버는 어휘만 내리고 한글 문구는 이 파일이 만든다.
 */

// 확장자를 적는 이유: 단위 시험이 이 파일을 node 에서 그대로 읽는다(node ESM 은 확장자를 추론하지 않는다)
import { addCalendarDays, addCalendarMonths, calendarDaysBetween, formatKst, formatKstIso } from './kstDate.js'
import { isStoreCd } from './subscriptionAdmin.js'
import { isMobileTel, telDigits, telText } from './telNumber.js'

/**
 * 사이드 메뉴 「사업장 관리 > 장비 신청」의 라우트 — appserver SQL 081 이 `sys_resources` 에 넣는 값과 같아야 메뉴 클릭이 빈 화면이
 * 되지 않는다(§12 081).
 */
export const EQUIPMENT_REQUEST_PATH = '/v/business/equipment-request/list'

/** 관리 API 의 뿌리 — 세션 경로(`ResourceServerConfig.SESSION_AUTH_API_PATHS`, §11.2) */
export const EQUIPMENT_REQUEST_API = '/api/admin/equipment-requests'

/** 위 카드 넷의 건수 */
export const EQUIPMENT_SUMMARY_API = `${EQUIPMENT_REQUEST_API}/summary`

/** 직접 추가 창의 미리 채움(§9.7) — 질의 `storeCd` */
export const EQUIPMENT_PREFILL_API = `${EQUIPMENT_REQUEST_API}/prefill`

/** 세션 액션 id — SQL 081 의 `sys_action` 여섯 + SQL 088 의 둘(직접 추가 §9.7)과 같은 문자열 */
export const EQUIPMENT_REQUEST_ACTION = Object.freeze({
  /** `GET /api/admin/equipment-requests` — 라우터 가드의 `meta.requiredAction` 이기도 하다(이 화면이 반드시 부르는 조회) */
  SEARCH: 'EquipmentRequestSearch',
  /** `GET …/summary` */
  SUMMARY: 'EquipmentRequestSummary',
  /** `POST …/{id}/contacted` */
  CONTACT: 'EquipmentRequestContact',
  /** `POST …/{id}/schedule` */
  SCHEDULE: 'EquipmentRequestSchedule',
  /** `POST …/{id}/installed` */
  INSTALL: 'EquipmentRequestInstall',
  /** `POST …/{id}/cancel` */
  CANCEL: 'EquipmentRequestCancel',
  /** `GET …/prefill` — 직접 추가 창(SQL 088) */
  PREFILL: 'EquipmentRequestPrefill',
  /** `POST /api/admin/equipment-requests` — 직접 추가(SQL 088) */
  ADD: 'EquipmentRequestAdd',
})

/** 신청 상태 — 서버 `status` 어휘 그대로(§9.2). 길: NEW → CONTACTED → SCHEDULED → INSTALLED, 열린 셋에서 CANCELED */
export const EQUIPMENT_STATUS = Object.freeze({
  NEW: 'NEW',
  CONTACTED: 'CONTACTED',
  SCHEDULED: 'SCHEDULED',
  INSTALLED: 'INSTALLED',
  CANCELED: 'CANCELED',
})

/** 단계 바꾸기 — 값이 곧 경로의 끝 조각이다(`POST …/{id}/{step}`) */
export const EQUIPMENT_STEP = Object.freeze({
  CONTACTED: 'contacted',
  SCHEDULE: 'schedule',
  INSTALLED: 'installed',
  CANCEL: 'cancel',
})

/** 그 사이 바뀜 — 서버 `OwnerError.STATUS_CHANGED`(409) */
export const STATUS_CHANGED_CODE = 'STATUS_CHANGED'

/** 그 매장에 열린 신청이 있다 — 서버 `OwnerError.REQUEST_OPEN`(409, 본문 `request` = 그 신청) */
export const REQUEST_OPEN_CODE = 'REQUEST_OPEN'

/** 설치 주소 상한 — 서버 `EquipmentRequestRules.ADDRESS_MAX`(`address VARCHAR(360)`) */
export const EQUIPMENT_ADDRESS_MAX = 360

/** 메모 상한 — 서버 `EquipmentRequestRules.MEMO_MAX`(`memo VARCHAR(500)`) */
export const EQUIPMENT_MEMO_MAX = 500

/** 목록의 기본 정렬 — 최근 신청부터(PDF 8쪽 목록 순서) */
export const EQUIPMENT_DEFAULT_SORT = Object.freeze({ sortProp: 'requestedDtm', sortOrder: 'descending' })

/**
 * 단계 바꾸기·직접 추가의 axios 옵트아웃 — **409(그 사이 바뀜·열린 신청)는 오류가 아니라 답이다**(§10.2·§9.7): 화면이 목록을 다시
 * 읽는 것으로 답하므로 인터셉터의 빨간 토스트가 함께 뜨면 화면이 두 말을 한다. 그래서 실패 문장은 화면(그 행·그 창)이 한 번만 말한다.
 */
export const EQUIPMENT_STEP_REQUEST_CONFIG = Object.freeze({ showErrorMessage: false })

/** 화면 문구 — i18n 키(한국어 문장 그대로) */
export const EQUIPMENT_TEXT = Object.freeze({
  title: '장비 신청',
  intro:
    '사장님이 POS 에서 보내거나 직원이 추가한 장비 설치 신청입니다. 연락 기한을 넘긴 새 신청은 신청 시각이 빨간 글씨로 보입니다.',
  statusNew: '새 신청',
  statusContacted: '연락 완료',
  statusScheduled: '설치일 확정',
  statusInstalled: '설치 완료',
  statusCanceled: '취소',
  cardNew: '새 신청 · 연락 전',
  cardContacted: '연락 완료 · 일정 조율',
  cardScheduled: '설치일 확정',
  cardInstalled: '이번 달 설치 완료',
  columnStatus: '상태',
  columnStore: '매장',
  columnRequested: '신청 시각',
  columnContact: '연락처',
  columnAddress: '주소',
  columnMemo: '메모',
  columnActions: '진행',
  stepContacted: '연락 완료로',
  stepSchedule: '설치일 입력',
  stepInstalled: '설치 완료로',
  stepCancel: '취소',
  filterStatus: '상태',
  filterAll: '전체',
  filterPeriod: '신청일',
  filterQuery: '검색어',
  filterQueryPlaceholder: '매장명 · 매장 ID · 연락처',
  filterOverdue: '기한 넘김만',
  empty: '조건에 맞는 장비 설치 신청이 없습니다.',
  detailContact: '연락처(전체)',
  /** 「(원래 010-****-5678)」 — 사장님이 신청 때 고친 연락처 옆에 붙는 등록돼 있던 번호(§9.6) */
  originalTel: '원래',
  detailCeo: '대표자',
  detailAddress: '설치 주소',
  detailMemo: '하실 말씀',
  detailHistory: '진행 기록',
  historyRequested: '신청',
  historyContacted: '연락 완료',
  historyScheduled: '설치일 확정',
  historyInstalled: '설치 완료',
  historyCanceled: '취소',
  scheduleTitle: '설치일 입력',
  scheduleLabel: '설치일',
  cancelTitle: '신청 취소',
  cancelLabel: '취소 사유',
  cancelPlaceholder: '취소하는 까닭을 적어 주십시오.',
  confirmContacted: '「연락 완료」로 바꿉니다. 되돌릴 수 없습니다.',
  confirmInstalled: '「설치 완료」로 바꿉니다. 되돌릴 수 없습니다.',
  save: '저장',
  close: '닫기',
  installDateRequired: '설치일을 골라 주십시오.',
  reasonRequired: '취소 사유를 적어 주십시오.',
  stepDone: '바꿨습니다.',
  statusChanged: '그 사이 다른 사람이 이 신청을 바꿨습니다. 목록을 다시 읽었습니다.',
  stepFailed: '요청을 처리하지 못했습니다.',
  // ── 직접 추가(§9.7) — 칸 이름 「매장」·「대표자」·「설치 주소」·「메모」는 위 낱말을 그대로 쓴다
  add: '신청 추가',
  addTitle: '장비 설치 신청 추가',
  addContact: '연락받을 휴대폰',
  /** 등록된 번호가 비었거나 휴대폰이 아닐 때의 자리 글자 — 유선 번호를 자리 글자로 보이면 비워 둬도 되는 것처럼 읽힌다(§9.6) */
  addContactPlaceholder: '010-0000-0000',
  addMemoPlaceholder: '설치 담당자가 알아야 할 것(선택)',
  /** 사용자 요청 문장 그대로(§9.7) */
  addNotice: '추가하면 사장님께 접수 알림톡이, 설치 담당자에게 새 신청 알림톡이 가요.',
  addCancel: '취소',
  addSubmit: '추가',
  addDone: '장비 설치 신청을 추가했습니다.',
  addFailed: '신청을 추가하지 못했습니다.',
  prefillFailed: '매장 정보를 읽지 못했습니다.',
  addOpen: '이 매장에는 진행 중인 신청이 있어 추가할 수 없습니다',
  storeRequired: '매장을 골라 주십시오.',
  addressRequired: '설치 주소를 넣어 주십시오.',
  addressTooLong: `설치 주소는 ${EQUIPMENT_ADDRESS_MAX}자까지입니다.`,
  contactRequired: '연락받을 휴대폰 번호를 넣어 주십시오.',
  contactNotMobile: '휴대폰 번호만 넣을 수 있습니다.',
  memoTooLong: `메모는 ${EQUIPMENT_MEMO_MAX}자까지입니다.`,
})

/** 상태 → 이름·색 — 색 이름은 화면의 배지 클래스 끝 조각이다 */
const STATUS_VIEWS = Object.freeze({
  [EQUIPMENT_STATUS.NEW]: Object.freeze({ label: EQUIPMENT_TEXT.statusNew, tone: 'new' }),
  [EQUIPMENT_STATUS.CONTACTED]: Object.freeze({ label: EQUIPMENT_TEXT.statusContacted, tone: 'contacted' }),
  [EQUIPMENT_STATUS.SCHEDULED]: Object.freeze({ label: EQUIPMENT_TEXT.statusScheduled, tone: 'scheduled' }),
  [EQUIPMENT_STATUS.INSTALLED]: Object.freeze({ label: EQUIPMENT_TEXT.statusInstalled, tone: 'installed' }),
  [EQUIPMENT_STATUS.CANCELED]: Object.freeze({ label: EQUIPMENT_TEXT.statusCanceled, tone: 'canceled' }),
})

/** 상태 거르기의 선택지 — 길의 순서 */
export const EQUIPMENT_STATUS_OPTIONS = Object.freeze(
  Object.keys(STATUS_VIEWS).map((status) => Object.freeze({ value: status, label: STATUS_VIEWS[status].label }))
)

/** 상태마다 다음 단계 하나 — 길이 한 줄이라 단추도 하나다(§10.2 「단계 단추」) */
const NEXT_STEPS = Object.freeze({
  [EQUIPMENT_STATUS.NEW]: Object.freeze({
    step: EQUIPMENT_STEP.CONTACTED,
    label: EQUIPMENT_TEXT.stepContacted,
    action: EQUIPMENT_REQUEST_ACTION.CONTACT,
  }),
  [EQUIPMENT_STATUS.CONTACTED]: Object.freeze({
    step: EQUIPMENT_STEP.SCHEDULE,
    label: EQUIPMENT_TEXT.stepSchedule,
    action: EQUIPMENT_REQUEST_ACTION.SCHEDULE,
  }),
  [EQUIPMENT_STATUS.SCHEDULED]: Object.freeze({
    step: EQUIPMENT_STEP.INSTALLED,
    label: EQUIPMENT_TEXT.stepInstalled,
    action: EQUIPMENT_REQUEST_ACTION.INSTALL,
  }),
})

/** 취소할 수 있는 상태 — 열린 셋(§9.3) */
const CANCELABLE = Object.freeze([EQUIPMENT_STATUS.NEW, EQUIPMENT_STATUS.CONTACTED, EQUIPMENT_STATUS.SCHEDULED])

/**
 * **행의 상태 배지** — 설치일이 정해진 신청은 날짜를 함께 말한다(「설치 10/17」 — PDF 8쪽). 모르는 상태는 받은 글자 그대로다
 * (새 상태가 서버에 먼저 생겨도 빈 칸이 되지 않는다). 순수.
 *
 * @param {?{status: string, installDate: ?string}} row
 * @returns {{label: string, tone: string}}
 */
export function statusViewOf(row) {
  const status = row && row.status
  const view = STATUS_VIEWS[status]
  if (!view) return { label: status ? String(status) : '', tone: 'unknown' }

  const date = status === EQUIPMENT_STATUS.SCHEDULED ? installDateText(row.installDate) : ''

  return date ? { label: `설치 ${date}`, tone: view.tone } : view
}

/** `YYYY-MM-DD` → `M/D`, 읽을 수 없으면 빈 글자 */
function installDateText(ymd) {
  const match = typeof ymd === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd) : null

  return match ? `${Number(match[2])}/${Number(match[3])}` : ''
}

/**
 * **이 행에서 할 수 있는 일** — 다음 단계 하나(없으면 `null`), 설치일 다시 정하기, 취소. 세션이 그 액션을 가질 때만 내준다 — 액션
 * 없는 단추는 누르면 403(= 로그아웃)이다. 순수(액션 판정 `can` 은 부르는 쪽이 세션으로 만든다).
 *
 * 설치일 다시 정하기(`reschedule`)는 설치일 확정(SCHEDULED) 행의 「설치일 입력」이다 — 연락 완료 행의 다음 단계와 같은 요청
 * (`schedule`)·같은 창이고, 서버가 SCHEDULED 에서도 받는다(10-21 E1).
 *
 * @param {?{status: string}} row
 * @param {function(string): boolean} can 세션이 그 액션을 갖는가
 * @returns {{next: ?{step: string, label: string}, reschedule: ?{step: string, label: string}, cancel: boolean}}
 */
export function rowActionsOf(row, can) {
  const status = row && row.status
  const next = NEXT_STEPS[status]
  const allowed = typeof can === 'function' ? can : () => false
  const reschedule = status === EQUIPMENT_STATUS.SCHEDULED && allowed(EQUIPMENT_REQUEST_ACTION.SCHEDULE)

  return {
    next: next && allowed(next.action) ? { step: next.step, label: next.label } : null,
    reschedule: reschedule ? { step: EQUIPMENT_STEP.SCHEDULE, label: EQUIPMENT_TEXT.stepSchedule } : null,
    cancel: CANCELABLE.indexOf(status) > -1 && allowed(EQUIPMENT_REQUEST_ACTION.CANCEL),
  }
}

/**
 * **단계 창의 처음 값** — 설치일 창은 그 행의 설치일을 미리 채운다(다시 정할 때 지금 날짜에서 고친다 — 처음 정할 때는 비어 있다).
 * 취소 사유는 늘 빈칸이다. 순수.
 *
 * @param {?{installDate: ?string}} row
 * @param {string} step [EQUIPMENT_STEP] 의 하나
 * @returns {{installDate: string, reason: string}}
 */
export function stepDraftOf(row, step) {
  const current = row && installDateText(row.installDate) ? row.installDate : ''

  return { installDate: step === EQUIPMENT_STEP.SCHEDULE ? current : '', reason: '' }
}

/**
 * **단계 바꾸기 요청** — 경로·본문, 또는 사람이 고쳐야 할 것(실패는 값으로). 설치일은 `YYYY-MM-DD` 여야 하고 취소 사유는 빈칸이
 * 아니어야 한다(서버도 400 으로 막는다 — 여기서는 보내기 전에 그 칸 아래에서 말한다). 순수.
 *
 * @param {string} step [EQUIPMENT_STEP] 의 하나
 * @param {number|string} requestId
 * @param {?{installDate?: string, reason?: string}} input
 * @returns {{ok: true, url: string, body: object}|{ok: false, problem: string}}
 */
export function stepRequestOf(step, requestId, input) {
  const url = `${EQUIPMENT_REQUEST_API}/${requestId}/${step}`
  const values = input || {}

  switch (step) {
    case EQUIPMENT_STEP.CONTACTED:
    case EQUIPMENT_STEP.INSTALLED:
      return { ok: true, url, body: {} }
    case EQUIPMENT_STEP.SCHEDULE:
      return installDateText(values.installDate)
        ? { ok: true, url, body: { installDate: values.installDate } }
        : { ok: false, problem: EQUIPMENT_TEXT.installDateRequired }
    case EQUIPMENT_STEP.CANCEL: {
      const reason = typeof values.reason === 'string' ? values.reason.trim() : ''

      return reason ? { ok: true, url, body: { reason } } : { ok: false, problem: EQUIPMENT_TEXT.reasonRequired }
    }
    default:
      return { ok: false, problem: EQUIPMENT_TEXT.stepFailed }
  }
}

/**
 * **단계 바꾸기 실패의 갈래** — 409 `STATUS_CHANGED` 는 「그 사이 바뀜」(화면은 다시 읽는다), 그 밖은 실패다. 순수.
 *
 * @param {*} error `HttpError`(인터셉터) — `status`·`data`(응답 본문)를 본다
 * @returns {boolean}
 */
export function isStatusChanged(error) {
  return conflictCodeOf(error) === STATUS_CHANGED_CODE
}

/**
 * **추가 실패의 갈래** — 409 `REQUEST_OPEN` 은 「그 매장에 열린 신청이 있다」(본문 `request` = 그 신청 — 창이 그 줄을 보이고 목록을
 * 다시 읽는다), 그 밖은 실패다. 순수.
 *
 * @param {*} error `HttpError`(인터셉터)
 * @returns {boolean}
 */
export function isRequestOpen(error) {
  return conflictCodeOf(error) === REQUEST_OPEN_CODE
}

/** 409 본문의 `code` — 409 가 아니거나 본문이 없으면 `null` */
function conflictCodeOf(error) {
  const body = error && error.data

  return !!error && error.status === 409 && !!body ? body.code : null
}

/**
 * **목록 조회 조건** — 거르기 넷을 계약의 질의로 옮긴다. 비어 있는 거르기는 키를 싣지 않는다(서버 기본 = 전체). 모르는 상태는
 * 싣지 않는다. 기간은 둘 다 있을 때만 싣는다. 순수 — 인자를 바꾸지 않는다.
 *
 * 설치 완료일(`installed`)은 「이번 달 설치 완료」 카드만 건다([cardFilterOf]) — 화면에 따로 칸이 없다.
 *
 * @param {?{status?: string, period?: ?Array<string>, q?: string, overdue?: boolean, installed?: ?Array<string>}} filter
 * @param {?object} paging `pagedListMixin.searchQuery()` 의 결과
 * @returns {object}
 */
export function equipmentListQuery(filter, paging) {
  const { status, period, q, overdue, installed } = filter || {}
  const query = {}

  if (STATUS_VIEWS[status]) query.status = status
  if (isRange(period)) Object.assign(query, { from: period[0], to: period[1] })
  if (isRange(installed)) Object.assign(query, { installedFrom: installed[0], installedTo: installed[1] })

  const text = typeof q === 'string' ? q.trim() : ''
  if (text) query.q = text
  if (overdue === true) query.overdue = true

  return Object.assign(query, paging || {})
}

/** 위 카드 넷 — 상태와 건수 칸(§11.2 summary). 넷째 칸은 「이번 달」 설치 완료다 */
const SUMMARY_CARDS = Object.freeze([
  Object.freeze({ status: EQUIPMENT_STATUS.NEW, label: EQUIPMENT_TEXT.cardNew, countKey: 'newCount' }),
  Object.freeze({
    status: EQUIPMENT_STATUS.CONTACTED,
    label: EQUIPMENT_TEXT.cardContacted,
    countKey: 'contactedCount',
  }),
  Object.freeze({
    status: EQUIPMENT_STATUS.SCHEDULED,
    label: EQUIPMENT_TEXT.cardScheduled,
    countKey: 'scheduledCount',
  }),
  Object.freeze({
    status: EQUIPMENT_STATUS.INSTALLED,
    label: EQUIPMENT_TEXT.cardInstalled,
    countKey: 'installedThisMonth',
  }),
])

/** 0 이상의 정수만 건수다 — 그 밖(칸 없음·요약을 못 읽음)은 `null`(화면은 건수를 그리지 않는다) */
const countOf = (value) =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 ? Math.floor(value) : null

/**
 * **위 카드 넷과 넘김 건수** — 요약 응답을 화면이 그릴 모양으로. 요약을 못 읽었으면 건수는 모름(`null`)이고 「–」로 말한다 — 0 으로
 * 그리지 않는다. 넘김 말은 넘김이 있을 때만이다(새 신청 카드 아래 빨간 글씨). 순수.
 *
 * @param {?object} summary `GET …/summary` 응답
 * @returns {{cards: Array<{status: string, label: string, count: ?number, countText: string}>, overdueCount: ?number,
 *   overdueText: string}}
 */
export function summaryCardsOf(summary) {
  const source = summary && typeof summary === 'object' ? summary : {}
  const overdueCount = countOf(source.overdueCount)

  return {
    cards: SUMMARY_CARDS.map((card) => {
      const count = countOf(source[card.countKey])

      return { status: card.status, label: card.label, count, countText: count === null ? '–' : `${count}건` }
    }),
    overdueCount,
    overdueText: overdueCount ? `기한 넘김 ${overdueCount}건` : '',
  }
}

/** 두 끝이 다 있는 기간 */
const isRange = (range) => Array.isArray(range) && !!range[0] && !!range[1]

/**
 * **이번 달 1일~말일(KST)** — 「이번 달 설치 완료」 카드의 설치 완료일 거르기. 요약 `installedThisMonth` 와 같은 달 경계다(서버가
 * KST 달로 센다). 말일은 다음 달 1일의 전날이다(윤년 2월 29일). 순수 — 지금은 부르는 쪽(화면)이 준다.
 *
 * @param {number} nowMs 지금
 * @returns {Array<string>} `['YYYY-MM-01', 'YYYY-MM-말일']`
 */
export function installedThisMonthOf(nowMs) {
  const first = `${formatKst('yyyy-MM', nowMs)}-01`

  return [first, addCalendarDays(addCalendarMonths(first, 1), -1)]
}

/**
 * **이 카드가 지금 목록을 거르고 있는가** — 그 상태만으로(넘김 거르기 없이) 거르고 있다. 「이번 달 설치 완료」는 설치 완료일 거르기도
 * 걸려 있어야 한다(상태 칸에서 설치 완료만 고른 목록은 지난달 것도 있어 카드 숫자와 다르다). 순수.
 *
 * @param {?{status?: string, overdue?: boolean, installed?: ?Array<string>}} filter
 * @param {string} status 카드의 상태
 * @returns {boolean}
 */
export function cardActiveOf(filter, status) {
  const current = filter || {}
  const installedRange =
    status === EQUIPMENT_STATUS.INSTALLED ? isRange(current.installed) : !isRange(current.installed)

  return current.status === status && !current.overdue && installedRange
}

/**
 * **카드를 누른 뒤의 거르기** — 그 상태로 거른다. 「이번 달 설치 완료」는 설치 완료일을 이번 달 1일~말일로 함께 걸어 목록 건수가
 * 카드 숫자와 같다(10-21 E3). 그 카드가 이미 거르고 있으면([cardActiveOf]) 상태·설치 완료일 거르기를 푼다(누름 = 켜고 끄기).
 * 신청일·검색어는 그대로다. 순수 — 새 객체를 돌려준다.
 *
 * @param {?{status?: string, overdue?: boolean, installed?: ?Array<string>}} filter 지금 거르기
 * @param {string} status 누른 카드의 상태
 * @param {number} nowMs 지금 — 이번 달을 정한다
 * @returns {object}
 */
export function cardFilterOf(filter, status, nowMs) {
  const current = filter || {}
  if (cardActiveOf(current, status)) return Object.assign({}, current, { status: '', overdue: false, installed: null })

  const installed = status === EQUIPMENT_STATUS.INSTALLED ? installedThisMonthOf(nowMs) : null

  return Object.assign({}, current, { status, overdue: false, installed })
}

/**
 * **사람이 거르기 칸(상태·신청일·검색어·기한 넘김)을 바꾼 뒤의 거르기** — 카드가 건 설치 완료일 거르기를 푼다. 그 거르기는 화면에
 * 칸이 없어 남겨 두면 보이지 않는 조건으로 목록을 줄인다. 순수 — 새 객체를 돌려준다.
 *
 * @param {?object} filter
 * @returns {object}
 */
export function editedFilterOf(filter) {
  return Object.assign({}, filter || {}, { installed: null })
}

/**
 * **신청 뒤 넘긴 시간** — 서버 계산값 `overdueHours` 가 0 이상의 수일 때만 그 정수, 아니면 `null`(넘김 아님). 순수.
 *
 * @param {?{overdueHours?: number}} row
 * @returns {?number}
 */
export function overdueHoursOf(row) {
  return countOf(row && row.overdueHours)
}

/**
 * **신청 시각 칸** — 시각의 말([requestedTimeText])에, 넘김이면 「(N시간)」을 붙이고 빨간 글씨 표지를 단다(PDF 8쪽 「어제 15:40
 * (18시간)」). 순수.
 *
 * @param {?{requestedDtm?: string, overdueHours?: number}} row
 * @param {number} nowMs 지금
 * @returns {{text: string, overdue: boolean}}
 */
export function requestedViewOf(row, nowMs) {
  const time = requestedTimeText(row && row.requestedDtm, nowMs)
  const hours = overdueHoursOf(row)

  return hours === null ? { text: time, overdue: false } : { text: `${time} (${hours}시간)`, overdue: true }
}

/**
 * **신청 시각의 말** — 오늘·어제는 그 낱말과 `HH:mm`, 올해는 `MM-dd HH:mm`, 그 밖은 `yyyy-MM-dd HH:mm`(PDF 8쪽). 날짜는 KST
 * 달력으로 견준다(보는 사람의 시간대와 무관 — `kstDate`). 읽을 수 없으면 빈 글자. 순수.
 *
 * @param {?string} iso 행의 `requestedDtm`(ISO-8601 `+09:00`)
 * @param {number} nowMs 지금 — 시계를 읽는 자리는 화면(경계)이다
 * @returns {string}
 */
export function requestedTimeText(iso, nowMs) {
  const ms = typeof iso === 'string' && iso ? Date.parse(iso) : NaN
  if (Number.isNaN(ms)) return ''

  const day = formatKst('yyyy-MM-dd', ms)
  const today = formatKst('yyyy-MM-dd', nowMs)
  const time = formatKst('HH:mm', ms)
  const daysAgo = calendarDaysBetween(day, today)

  if (daysAgo === 0) return `오늘 ${time}`
  if (daysAgo === 1) return `어제 ${time}`
  if (day.slice(0, 4) === today.slice(0, 4)) return formatKst('MM-dd HH:mm', ms)

  return formatKst('yyyy-MM-dd HH:mm', ms)
}

/**
 * **목록에 보일 가린 연락처** — 번호 종류로 끊은 뒤(`telNumber.telText`) 가운데 묶음을 `*` 로 가린다(`010-****-5678`). 끊지 못한
 * 번호는 끝 4자리만 남긴다. 전체 번호는 행을 펼치면 보인다(담당자가 전화할 번호다 — §10.2). 빈 값은 빈 글자. 순수.
 *
 * @param {?string} tel
 * @returns {string}
 */
export function maskedTel(tel) {
  const text = telText(tel)
  const groups = text.split('-')

  if (groups.length === 3 && groups.every((group) => /^[0-9]+$/.test(group))) {
    return [groups[0], '*'.repeat(groups[1].length), groups[2]].join('-')
  }

  const digits = telDigits(text)

  return digits.length > 4 ? '*'.repeat(digits.length - 4) + digits.slice(-4) : text
}

/**
 * **원래 연락처** — 신청 때 오더엔에 등록돼 있던 번호(`registeredTel`, §9.6)가 신청의 연락처(`contactTel` — 사장님이 확인하거나 고친
 * 번호, 전화할 번호)와 **다를 때만** 그 번호, 아니면 `null`(화면은 「(원래 …)」를 붙이지 않는다). 같은지는 숫자로 견준다 — 표기만 다른
 * 번호는 같은 번호다. 등록된 번호가 비었거나 이 칸이 생기기 전 줄(`null`·칸 없음)도 `null` 이다. 순수.
 *
 * @param {?{contactTel?: ?string, registeredTel?: ?string}} row
 * @returns {?string} 등록돼 있던 번호(받은 글자 그대로 — 가리기·끊기는 화면이 [maskedTel]·`telText` 로)
 */
export function originalTelOf(row) {
  const registered = telDigits(row && row.registeredTel)
  if (!registered || registered === telDigits(row.contactTel)) return null

  return row.registeredTel
}

/**
 * 진행 기록의 줄 — 시각 칸 · 누가 칸 · 덧붙일 말. 「신청」의 누가(`requestedBy`)는 POS 신청이면 POS 로그인 계정, 직원 추가(§9.7)면
 * 관리자 계정 아이디다 — 그 값으로 두 출처가 갈린다
 */
const HISTORY_STEPS = Object.freeze([
  Object.freeze({ label: EQUIPMENT_TEXT.historyRequested, at: 'requestedDtm', by: 'requestedBy' }),
  Object.freeze({ label: EQUIPMENT_TEXT.historyContacted, at: 'contactedDtm', by: 'contactedBy' }),
  Object.freeze({ label: EQUIPMENT_TEXT.historyScheduled, at: 'scheduledDtm', by: 'scheduledBy', note: 'installDate' }),
  Object.freeze({ label: EQUIPMENT_TEXT.historyInstalled, at: 'installedDtm', by: 'installedBy' }),
  Object.freeze({ label: EQUIPMENT_TEXT.historyCanceled, at: 'canceledDtm', by: 'canceledBy', note: 'cancelReason' }),
])

/**
 * **진행 기록** — 단계마다 누가·언제(§9.2 감사 칸). 시각이 있는 단계만, 길의 순서로. 순수.
 *
 * @param {?object} row
 * @returns {Array<{label: string, at: string, by: string, note: string}>}
 */
export function historyOf(row) {
  const source = row || {}
  const text = (key) => (key && typeof source[key] === 'string' ? source[key].trim() : '')

  return HISTORY_STEPS.map((step) => ({ step, at: formatKstIso('yyyy-MM-dd HH:mm', source[step.at]) }))
    .filter((entry) => entry.at)
    .map(({ step, at }) => ({ label: step.label, at, by: text(step.by), note: text(step.note) }))
}

// ── 직접 추가(§9.7) ─────────────────────────────────────────────────────────

/** 앞뒤 공백을 걷은 글자 — 글자가 아니면 빈 글자 */
const trimmedOf = (value) => (typeof value === 'string' ? value.trim() : '')

/** 글자 수 — 서버 `codePointCount` 와 같은 셈(이모지 하나 = 한 자) */
const lengthOf = (text) => Array.from(text).length

/**
 * **추가 창의 처음 값** — 미리 채움에서: 설치 주소는 등록 주소, 연락받을 휴대폰은 등록 번호가 휴대폰이면 그 번호(끊어 보인 표기),
 * 아니면(`contactNeeded`) 빈칸(입력 필수), 메모는 빈칸. 「휴대폰인가」는 서버가 정한 `contactNeeded` 를 읽는다. 순수.
 *
 * @param {?{registeredTel?: ?string, address?: ?string, contactNeeded?: boolean}} prefill `GET …/prefill` 응답
 * @returns {{address: string, contactTel: string, memo: string}}
 */
export function addDraftOf(prefill) {
  const source = prefill || {}

  return {
    address: typeof source.address === 'string' ? source.address : '',
    contactTel: source.contactNeeded ? '' : telText(source.registeredTel),
    memo: '',
  }
}

/**
 * **연락받을 휴대폰 칸의 자리 글자** — 등록 번호가 휴대폰이면 그 번호(칸을 비우면 서버가 그 번호로 연락한다), 아니면 휴대폰 모양
 * (「010-0000-0000」 — 유선 번호를 보이면 비워 둬도 되는 것처럼 읽힌다, §9.6). 순수.
 *
 * @param {?{registeredTel?: ?string, contactNeeded?: boolean}} prefill
 * @returns {string}
 */
export function contactPlaceholderOf(prefill) {
  const source = prefill || {}

  return source.contactNeeded ? EQUIPMENT_TEXT.addContactPlaceholder : telText(source.registeredTel)
}

/**
 * **열린 신청 한 줄** — 그 매장에 진행 중인 신청이 있으면 「추가할 수 없다 — 상태 · 신청 시각」, 없으면 빈 글자(창은 [추가]를 연다).
 * 미리 채움의 `open` 과 409 `REQUEST_OPEN` 본문의 `request` 가 같은 모양(관리 행)이라 같은 말이다. 순수.
 *
 * @param {?{status?: string, installDate?: ?string, requestedDtm?: string}} open
 * @param {number} nowMs 지금
 * @returns {string}
 */
export function openRequestTextOf(open, nowMs) {
  if (!open || typeof open !== 'object') return ''

  const time = requestedTimeText(open.requestedDtm, nowMs)
  const parts = [statusViewOf(open).label, time ? `${time} 신청` : ''].filter(Boolean)

  return `${EQUIPMENT_TEXT.addOpen} — ${parts.join(' · ')}`
}

/**
 * **추가 요청** — 경로·본문, 또는 사람이 고칠 칸과 말(실패는 값으로). 서버 `EquipmentRequestRules.submission` 의 거울이다(서버가
 * 정본 — 여기서는 보내기 전에 그 칸 아래에서 말한다):
 *
 * - 매장 — 미리 채움을 읽은 매장(`prefill.storeCd`). 창에 보이는 매장이 보내는 매장이다.
 * - 설치 주소 — 앞뒤 공백을 걷고 1~[EQUIPMENT_ADDRESS_MAX]자. 늘 싣는다.
 * - 연락받을 휴대폰 — 넣었으면 휴대폰이어야 하고 숫자만 싣는다. 비었으면 싣지 않는다(서버가 등록 번호로 연락한다) — 단 `contactNeeded`
 *   면 필수다.
 * - 메모 — 앞뒤 공백을 걷고 [EQUIPMENT_MEMO_MAX]자까지, 비면 싣지 않는다.
 *
 * 멱등 키는 부르는 쪽(창 — 경계)이 만든다. 순수 — 인자를 바꾸지 않는다.
 *
 * @param {?{storeCd?: number, contactNeeded?: boolean}} prefill
 * @param {?{address?: string, contactTel?: string, memo?: string}} draft
 * @param {string} idempotencyKey
 * @returns {{ok: true, url: string, body: object}|{ok: false, field: string, problem: string}}
 */
export function addRequestOf(prefill, draft, idempotencyKey) {
  const source = prefill || {}
  const values = draft || {}
  const reject = (field, problem) => ({ ok: false, field, problem })

  if (!isStoreCd(source.storeCd)) return reject('storeCd', EQUIPMENT_TEXT.storeRequired)

  const address = trimmedOf(values.address)
  if (!address) return reject('address', EQUIPMENT_TEXT.addressRequired)
  if (lengthOf(address) > EQUIPMENT_ADDRESS_MAX) return reject('address', EQUIPMENT_TEXT.addressTooLong)

  const contactRaw = trimmedOf(values.contactTel)
  const contactTel = telDigits(contactRaw)
  if (contactRaw && !isMobileTel(contactTel)) return reject('contactTel', EQUIPMENT_TEXT.contactNotMobile)
  if (!contactRaw && source.contactNeeded) return reject('contactTel', EQUIPMENT_TEXT.contactRequired)

  const memo = trimmedOf(values.memo)
  if (lengthOf(memo) > EQUIPMENT_MEMO_MAX) return reject('memo', EQUIPMENT_TEXT.memoTooLong)

  const body = { storeCd: source.storeCd, idempotencyKey, address }
  if (contactTel) body.contactTel = contactTel
  if (memo) body.memo = memo

  return { ok: true, url: EQUIPMENT_REQUEST_API, body }
}
